import { Component, HostListener, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IconComponent } from './ui/icon.component';
import { AnimateDirective } from '../directives/animate.directive';

@Component({
  selector: 'app-hero-v2',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, AnimateDirective],
  template: `
    <section id="hero" class="relative min-h-[100vh] flex flex-col justify-center pt-12 pb-12 md:pt-16 md:pb-20 bg-[#111111] overflow-hidden">
      <!-- Gradient Fade -->
      <div class="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/95 to-transparent md:to-[#111111]/10 pointer-events-none"></div>

      <!-- Left Side Decorative Image (Inverted) with Parallax -->
      <div class="absolute top-0 left-0 w-full md:w-[50%] h-full z-0 pointer-events-none opacity-40 mix-blend-screen will-change-transform"
           [style.transform]="'translateY(' + (scrollY() * 0.05) + 'px)'">
         <img src="/images/fundo-1.svg" 
               class="w-full h-full object-cover md:object-contain object-left-top transform -scale-x-100" 
               alt=""
               aria-hidden="true"
               width="600"
               height="600">
      </div>

      <!-- Decorative Orange Blur -->
      <div class="absolute -bottom-24 -left-24 w-96 h-96 bg-[#FF5500] rounded-full blur-[128px] opacity-10"></div>

      <div class="container mx-auto px-4 md:px-6 relative z-10">
        <!-- Flex container to keep elements united in the center regardless of screen width -->
        <div class="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16 max-w-6xl mx-auto">
          
          <!-- Content -->
          <div class="w-full max-w-xl space-y-5 lg:space-y-6">
            <div appAnimate>
              <img src="/images/logo-branca.svg" alt="beetrack" class="h-12 w-auto" width="180" height="48">
            </div>
            <div appAnimate class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5500]/10 border border-[#FF5500]/20 text-[#FF5500] text-xs font-semibold tracking-wide uppercase">
              🔴 ALERTA: FISCALIZAÇÃO DO MTE INTENSIFICADA EM 2026
            </div>

            <h1 appAnimate [delay]="100" class="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-[1.1] tracking-tight">
              Uma única autuação trabalhista pode custar mais de R$ 80 mil à sua operação.
            </h1>

            <p appAnimate [delay]="200" class="text-base md:text-lg text-gray-200 font-medium leading-tight">
              A beetrack monitora automaticamente as pausas térmicas obrigatórias da NR 36 — e garante que você nunca seja pego desprevenido.
            </p>

            <p appAnimate [delay]="300" class="text-sm md:text-base text-gray-400 leading-relaxed">
              Sem planilha. Sem fiscal interno. Sem risco. Seu controle de pausa térmica 100% automatizado, documentado e juridicamente válido.
            </p>

          </div>

          <!-- Image/Visual -->
          <div appAnimate [delay]="300" class="w-full max-w-md lg:max-w-[480px] relative mt-8 lg:mt-0 flex justify-center">
            <div class="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
              <!-- Overlay gradient -->
              <div class="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent z-10 opacity-40"></div>
              
              <!-- Industrial Image -->
              <img src="/images/hero-v2-md.webp"
                   srcset="/images/hero-v2-sm.webp 400w, /images/hero-v2-md.webp 600w, /images/hero-v2-lg.webp 1000w"
                   sizes="(max-width: 768px) 100vw, 480px"
                   alt="Trabalhador em ambiente industrial refrigerado" 
                   width="480"
                   height="600"
                   fetchpriority="high"
                   class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 filter saturate-50 contrast-125" />
              
              <!-- Floating UI Card Mockup -->
              <div class="absolute bottom-6 left-6 right-6 z-20 bg-[#1c1c1c]/95 backdrop-blur-md border border-white/10 p-4 md:p-5 rounded-xl shadow-lg">
                 <div class="flex items-center justify-between mb-3">
                   <div class="flex items-center gap-3">
                     <div class="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                       <app-icon name="thermometer" [size]="20"></app-icon>
                     </div>
                     <div>
                       <div class="text-[10px] text-gray-300 uppercase tracking-wider">Câmara Resfriada 02</div>
                       <div class="text-sm md:text-base font-bold text-white">09°C (Monitorado)</div>
                     </div>
                   </div>
                   <div class="px-2 py-1 rounded bg-green-500/20 text-green-400 text-[10px] font-bold uppercase tracking-wide">LEGAL</div>
                 </div>
                 <div class="space-y-2">
                   <div class="flex justify-between text-sm text-gray-300">
                     <span>Tempo Restante</span>
                     <span class="text-white font-mono font-bold">00:45:00</span>
                   </div>
                   <div class="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                     <div class="bg-[#FF5500] h-full rounded-full w-3/4"></div>
                   </div>
                 </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  `
})
export class HeroV2Component {
  scrollY = signal(0);

  @HostListener('window:scroll')
  onScroll() {
    this.scrollY.set(window.scrollY);
  }

  scrollToCta() {
    const el = document.getElementById('cta');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
